import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
import AdminToggle from '../../components/AdminToggle';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import LHDataTable from '../../../../components/LHDataTable';
import { t } from './i18n';
import { EquipmentLanguage } from './types';
import { ListLanguageToggle } from './EquipmentConfig';
import { PhotoCategoryItem } from './types';
import AdminSelect from '../../components/AdminSelect';
import AdminMultiSelect from '../../components/AdminMultiSelect';
import {
  fetchPhotoCategories,
  createPhotoCategory,
  updatePhotoCategory,
  togglePhotoCategoryActive,
  checkPhotoCategoryExists,
  checkPhotoDisplayOrderExists,
  fetchRouteTypes,
} from './perfectTruckApi';
import { useToast } from '../../../../components/LhToastContainer';

const MAX_ACTIVE = 6;

const ORG_TYPE_OPTIONS = [
  { label: 'PBNA', value: 'PBNA' },
  { label: 'FLNA', value: 'FLNA' },
  { label: '1NA', value: '1NA' },
];

interface ErrorMap { [key: string]: string }

const ALL_ROUTE_TYPE_VALUE = '';

const normalizeRouteTypeToArray = (routeType: string | string[] | null | undefined): string[] => {
  const rawValues = Array.isArray(routeType)
    ? routeType.map(v => String(v).trim())
    : !routeType
      ? []
      : String(routeType)
          .split(',')
          .map(v => v.trim());

  const hasAll = rawValues.some(v => v === '' || v.toLowerCase() === 'all');
  if (hasAll) {
    return [];
  }

  return rawValues.filter(Boolean);
};

const formatRouteType = (routeType: string | string[] | null | undefined, options: Array<{ label: string; value: string }> = []): string => {
  const values = normalizeRouteTypeToArray(routeType);
  if (values.length === 0) return 'All';
  // Route type is persisted as an ID; legacy entries stored the label directly.
  return values.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');
};

const PhotoConfig: React.FC<{ listLanguage?: EquipmentLanguage }> = ({ listLanguage }) => {
  const currentListLanguage = listLanguage ?? 'en';
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ photoLabel: '' });
  const [frForm, setFrForm] = useState({ photoLabel: '' });
  const [esForm, setEsForm] = useState({ photoLabel: '' });
  const [modalValidationError, setModalValidationError] = useState<string>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [items, setItems] = useState<PhotoCategoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [editItem, setEditItem] = useState<PhotoCategoryItem | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formLabel, setFormLabel] = useState('');
  const [formOrder, setFormOrder] = useState('');
  const [formRouteType, setFormRouteType] = useState<string[]>([]);
  const [formIsDuo, setFormIsDuo] = useState(false);
  const [formIs3pl, setFormIs3pl] = useState(false);
  const [formOrgType, setFormOrgType] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [globalError, setGlobalError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [routeTypeOptions, setRouteTypeOptions] = useState<Array<{ label: string; value: string }>>([]);
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) =>
    state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? ''
  );

  const modalVisible = isAdding || editItem !== null;

  const loadItems = useCallback(async () => {
    try {
      console.debug('[PhotoConfig] loadItems called, listLanguage=', currentListLanguage);
      setIsLoading(true);
      setApiError('');
      // Load list data using current list language so API is called with language filter
      const [photoCategoriesData, routeTypesData] = await Promise.all([
        fetchPhotoCategories(currentListLanguage),
        fetchRouteTypes(currentListLanguage),
      ]);
      setItems(photoCategoriesData);
      const routeOptions = [
        { label: 'All', value: '' },
        ...routeTypesData.map(rt => ({
          label: rt.routeType,
          value: String(rt.id),
        })),
      ];
      setRouteTypeOptions(routeOptions);
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to load photo categories.');
    } finally {
      setIsLoading(false);
    }
  }, [listLanguage]);

  useEffect(() => { loadItems(); }, [listLanguage]);

  const validate = (label: string, order: string, currentId?: number): ErrorMap => {
    const errs: ErrorMap = {};
    if (!label.trim()) { errs.label = 'Photo label is required.'; }
    else if (label.length > 50) { errs.label = 'Photo label cannot exceed 50 characters.'; }
    else {
      const dup = items.find(i => i.photoLabel.toLowerCase() === label.trim().toLowerCase() && i.id !== currentId);
      if (dup) errs.label = `"${label.trim()}" already exists.`;
    }
    const ord = parseInt(order);
    if (!ord || ord < 1) { errs.order = 'Display order must be a positive integer.'; }
    else {
      const dupOrd = items.find(i => i.displayOrder === ord && i.id !== currentId);
      if (dupOrd) errs.order = `Display order ${ord} is already in use.`;
    }
    return errs;
  };

  const handleToggleActive = async (id: number) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    if (!item.isActive) {
      const activeCount = items.filter(i => i.isActive).length;
      if (activeCount >= MAX_ACTIVE) {
        setGlobalError(`Maximum of ${MAX_ACTIVE} active photo categories allowed.`);
        return;
      }
    }
    setGlobalError('');
    try {
      const updated = await togglePhotoCategoryActive(id, !item.isActive, requestedBy);
      setItems(prev => prev.map(i => i.id === id ? updated : i));
      toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.photo'), variant: 'success' });
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to toggle active status.');
    }
  };

  const openAdd = () => {
    setEditItem(null);
    setIsAdding(true);
    setFormLabel('');
    setFormOrder(String(items.length + 1));
    setFormRouteType([]);
    setFormIsDuo(false);
    setFormIs3pl(false);
    setFormOrgType('');
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
    setModalLanguage('en');
    setEnForm({ photoLabel: '' }); setFrForm({ photoLabel: '' }); setEsForm({ photoLabel: '' });
    setModalValidationError('');
  };

  const openEdit = (item: PhotoCategoryItem) => {
    setIsAdding(false);
    setEditItem(item);
    setFormLabel(item.photoLabel);
    setFormOrder(String(item.displayOrder));
    // routeType is persisted as an ID; resolve legacy label-based entries to IDs so the
    // multi-select correctly reflects the current selection.
    const resolvedRouteTypes = normalizeRouteTypeToArray(item.routeType as any).map(raw => {
      const byId = routeTypeOptions.find(o => o.value === raw);
      if (byId) return byId.value;
      const byLabel = routeTypeOptions.find(o => o.label.trim().toLowerCase() === raw.trim().toLowerCase());
      return byLabel ? byLabel.value : raw;
    });
    setFormRouteType(resolvedRouteTypes);
    setFormIsDuo(item.isDuo ?? false);
    setFormIs3pl(item.is3pl ?? false);
    setFormOrgType(item.organizationType ?? '');
    setFormIsActive(item.isActive);
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
    setModalLanguage('en');
    setModalValidationError('');
    // Load translations across languages for this item
    (async () => {
      try {
        setModalLoading(true);
        const [enRes, frRes, esRes] = await Promise.all([
          fetchPhotoCategories('en').catch(() => []),
          fetchPhotoCategories('fr').catch(() => []),
          fetchPhotoCategories('es').catch(() => []),
        ]);
        const findById = (arr: PhotoCategoryItem[]) => arr.find(r => r.id === item.id);
        const en = findById(enRes as PhotoCategoryItem[]);
        const fr = findById(frRes as PhotoCategoryItem[]);
        const es = findById(esRes as PhotoCategoryItem[]);
        setEnForm({ photoLabel: en?.photoLabel ?? item.photoLabel });
        setFrForm({ photoLabel: fr?.photoLabel ?? '' });
        setEsForm({ photoLabel: es?.photoLabel ?? '' });
      } catch (e) {
        // ignore
      } finally {
        setModalLoading(false);
      }
    })();
  };

  const doClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      setIsAdding(false);
      setEditItem(null);
      setFormLabel('');
      setFormOrder('');
      setErrors({});
      setHasUnsavedChanges(false);
      setModalLanguage('en');
    }
  };

  const confirmClose = () => {
    setIsAdding(false);
    setEditItem(null);
    setFormLabel('');
    setFormOrder('');
    setFormRouteType([]);
    setErrors({});
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
    setModalLanguage('en');
  };

  const handleSave = async () => {
    const englishLabel = (enForm.photoLabel ?? '').trim();
    const errs = validate(englishLabel, formOrder, editItem?.id);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const ord = parseInt(formOrder);
    // validate translations for all languages
    const translations = { en: enForm, fr: frForm, es: esForm } as any;
    const missing: string[] = [];
    (['en','fr','es'] as EquipmentLanguage[]).forEach(lang => {
      const tform = translations[lang];
      if (!tform || !tform.photoLabel || !tform.photoLabel.trim()) missing.push(lang);
    });
    if (missing.length > 0) {
      setModalValidationError(t(modalLanguage, 'validation.completeTranslations'));
      return;
    }
    try {
      setIsSaving(true);
      if (!editItem) {
        const [labelExists, orderExists] = await Promise.all([
          checkPhotoCategoryExists(englishLabel),
          checkPhotoDisplayOrderExists(ord),
        ]);
        const checkErrs: ErrorMap = {};
        if (labelExists) checkErrs.label = 'Photo Category Already Exists';
        if (orderExists) checkErrs.order = 'Display Order already exists';
        if (Object.keys(checkErrs).length > 0) {
          setErrors(checkErrs);
          setIsSaving(false);
          return;
        }
      }
      const normalizedRouteTypes = normalizeRouteTypeToArray(formRouteType as any);
      // Build multilingual payloads (array of 3 objects: en, fr, es)
      const commonPayload = {
        displayOrder: ord,
        isActive: formIsActive,
        routeType: normalizedRouteTypes.length > 0 ? normalizedRouteTypes : [],
        isDuo: formIsDuo,
        is3pl: formIs3pl,
        organizationType: (formOrgType || null) as any,
        requestedBy,
      } as any;

      const make = (lang: EquipmentLanguage, f: any) => ({
        photoLabel: (f.photoLabel ?? '').trim(),
        displayOrder: commonPayload.displayOrder,
        isActive: commonPayload.isActive,
        routeType: commonPayload.routeType,
        isDuo: commonPayload.isDuo,
        is3pl: commonPayload.is3pl,
        organizationType: commonPayload.organizationType,
        requestedBy: commonPayload.requestedBy,
        language: lang,
      });

      const payloadArray = [make('en', enForm), make('fr', frForm), make('es', esForm)];

      if (editItem) {
        const updated = await updatePhotoCategory(editItem.id, payloadArray as any);
        setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.photo'), variant: 'success' });
      } else {
        const created = await createPhotoCategory(payloadArray as any);
        setItems(prev => [...prev, created]);
        toast.showToast({ header: t(currentListLanguage, 'toast.addSuccess.photo'), variant: 'success' });
      }
      confirmClose();
    } catch (e: any) {
      setApiError(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to save photo category.');
    } finally {
      setIsSaving(false);
    }
  };

  const sorted = [...items].sort((a, b) => a.displayOrder - b.displayOrder);
  const filtered = searchQuery.trim()
    ? sorted.filter(i => i.photoLabel.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : sorted;

  return (
    <View>
      {/* Info banner */}
      <View style={styles.infoBanner}>
        <MaterialIcons name="check" size={16} color={AdminColors.blue700} style={styles.infoIcon} />
        <Text style={styles.infoText}>{t(currentListLanguage, 'info.photoBanner')}</Text>
      </View>

      {/* Toolbar: search + add */}
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <MaterialIcons name="search" size={16} color={AdminColors.textMuted} style={{ marginRight: 6 }} />
          <TextInput
            style={styles.searchInput}
            placeholder={t(currentListLanguage, 'searchPlaceholder')}
            placeholderTextColor={AdminColors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7}>
              <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        <AdminButton
          label={t(currentListLanguage, 'addButton.photo')}
          variant="primary"
          small
          onPress={openAdd}
        />
      </View>

      {globalError ? (
        <Text style={styles.globalError}>{globalError}</Text>
      ) : null}

      {isLoading ? (
        <View style={styles.loadingWrapper}>
          <ActivityIndicator size="large" color={AdminColors.blue700} />
        </View>
      ) : (
      <View style={styles.surface}>
        <LHDataTable
          data={filtered}
          columns={[
            { columnKey: 'photoLabel', header: t(currentListLanguage, 'table.photoLabel'), flexGrow: 2, sortable: true },
            { columnKey: 'displayOrder', header: t(currentListLanguage, 'table.displayOrder'), width: 110, sortable: true },
            {
              columnKey: 'routeType',
              header: t(currentListLanguage, 'table.routeType'),
              width: 140,
              sortable: true,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{formatRouteType(item.routeType, routeTypeOptions)}</Text>
              ),
            },
            {
              columnKey: 'isDuo',
              header: t(currentListLanguage, 'table.duo'),
              width: 80,
              sortable: false,
              renderCell: (item: any) => (
                  <Text style={styles.cellText}>{item.isDuo ? t(currentListLanguage, 'label.yes') : t(currentListLanguage, 'label.no')}</Text>
                ),
            },
            {
              columnKey: 'is3pl',
              header: t(currentListLanguage, 'table.3pl'),
              width: 80,
              sortable: false,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.is3pl ? 'Yes' : 'No'}</Text>
              ),
            },
            {
              columnKey: 'organizationType',
              header: t(currentListLanguage, 'table.org'),
              width: 100,
              sortable: true,
              renderCell: (item: any) => (
                <Text style={styles.cellText}>{item.organizationType || '—'}</Text>
              ),
            },
            {
              columnKey: 'isActive',
              header: t(currentListLanguage, 'table.active'),
              width: 100,
              sortable: false,
              renderCell: (item: any) => (
                <TouchableOpacity onPress={() => handleToggleActive(item.id)} activeOpacity={0.7}>
                  <View style={[styles.statusBadge, item.isActive ? styles.statusActive : styles.statusInactive]}>
                    <Text style={[styles.statusBadgeText, item.isActive ? styles.statusActiveText : styles.statusInactiveText]}>
                      {item.isActive ? t(currentListLanguage, 'counts.active') : t(currentListLanguage, 'counts.inactive')}
                    </Text>
                  </View>
                </TouchableOpacity>
              ),
            },
          ]}
          tableHeader=""
          tableDescription={`Showing ${filtered.length} of ${items.length} Photo Categories (${items.filter(i => i.isActive).length} ${t(currentListLanguage, 'counts.active')}, max ${MAX_ACTIVE})`}
          showHeaderTopBorder={false}
          onRowClick={(item) => openEdit(item as PhotoCategoryItem)}
          disableRowHighlight
          locale={{
            rowsPerPageLabel: t(currentListLanguage, 'pagination.rowsPerPage'),
            pageLabel: t(currentListLanguage, 'pagination.page'),
            pageOfLabel: t(currentListLanguage, 'pagination.of'),
            firstPageLabel: t(currentListLanguage, 'pagination.first'),
            prevPageLabel: t(currentListLanguage, 'pagination.previous'),
            nextPageLabel: t(currentListLanguage, 'pagination.next'),
            lastPageLabel: t(currentListLanguage, 'pagination.last'),
          }}
        />
      </View>
      )}

      {/* Add / Edit Modal */}
      <AdminModal
        visible={modalVisible}
        title={editItem ? t(modalLanguage, 'modal.editTitle.photo') : t(modalLanguage, 'modal.addTitle.photo')}
        onClose={doClose}
        footer={
          <>
            <AdminButton label={t(modalLanguage, 'button.cancel')} variant="secondary" onPress={doClose} />
            <AdminButton label={editItem ? t(modalLanguage, 'button.saveChanges') : t(modalLanguage, 'button.save')} variant="primary" onPress={handleSave} disabled={isSaving} />
          </>
        }
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <View style={{ flex: 1, alignItems: 'flex-start' }}>
            {modalValidationError ? (
              <View style={[styles.errorBanner]}> 
                <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
                <Text style={styles.errorText}>{modalValidationError}</Text>
              </View>
            ) : null}
          </View>
            <View style={{ marginLeft: 12 }}>
              <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setModalValidationError(''); }} compact />
            </View>
        </View>
        {apiError ? (
          <Text style={styles.modalError}>{apiError}</Text>
        ) : null}
        <AdminFormField label={t(modalLanguage, 'table.photoLabel')} required error={errors.label}>
          <AdminTextInput
            value={(modalLanguage === 'en' ? enForm.photoLabel : modalLanguage === 'fr' ? frForm.photoLabel : esForm.photoLabel)}
            onChangeText={v => {
              if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, photoLabel: v }));
              else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, photoLabel: v }));
              else setEsForm(prev => ({ ...prev, photoLabel: v }));
              setErrors({}); setHasUnsavedChanges(true); setModalValidationError('');
            }}
            placeholder={t(modalLanguage, 'placeholder.example')}
            hasError={!!errors.label || !!modalValidationError}
          />
        </AdminFormField>
        <AdminFormField label={t(modalLanguage, 'form.displayOrder')} required error={errors.order}>
          <AdminTextInput
            value={formOrder}
            onChangeText={v => { setFormOrder(v); setErrors({}); setHasUnsavedChanges(true); }}
            keyboardType="number-pad"
            placeholder="1"
            hasError={!!errors.order}
          />
        </AdminFormField>
        <AdminFormField label={t(modalLanguage, 'form.routeType')}>
          <AdminMultiSelect
            values={formRouteType.length > 0 ? formRouteType : [ALL_ROUTE_TYPE_VALUE]}
            onChange={(vals: string[]) => {
              const hasAll = vals.includes(ALL_ROUTE_TYPE_VALUE);
              if (hasAll) {
                setFormRouteType([]);
              } else {
                const deduped = Array.from(new Set(vals.filter(Boolean)));
                setFormRouteType(deduped);
              }
              setHasUnsavedChanges(true);
            }}
            options={routeTypeOptions}
            allValue={ALL_ROUTE_TYPE_VALUE}
          />
        </AdminFormField>
        <AdminFormField label={t(modalLanguage, 'form.orgType')}>
          <AdminSelect
            value={formOrgType}
            onChange={v => { setFormOrgType(v); setHasUnsavedChanges(true); }}
            options={ORG_TYPE_OPTIONS}
            placeholder={t(modalLanguage, 'placeholder.selectOrg')}
          />
        </AdminFormField>
        <View style={styles.toggleRow}>
          <Text style={styles.toggleLabel}>{t(modalLanguage, 'table.duo')}</Text>
          <AdminToggle value={formIsDuo} onChange={v => { setFormIsDuo(v); setHasUnsavedChanges(true); }} />
        </View>
        <View style={styles.toggleRow}>
          <Text style={styles.toggleLabel}>{t(modalLanguage, 'table.3pl')}</Text>
          <AdminToggle value={formIs3pl} onChange={v => { setFormIs3pl(v); setHasUnsavedChanges(true); }} />
        </View>
        {editItem ? (
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>{t(modalLanguage, 'form.active')}</Text>
            <AdminToggle value={formIsActive} onChange={v => { setFormIsActive(v); setHasUnsavedChanges(true); }} />
          </View>
        ) : null}
      </AdminModal>

      {/* Unsaved Changes Confirmation Modal */}
      <AdminModal
        visible={showConfirmClose}
        title={t(currentListLanguage, 'modal.unsavedTitle')}
        onClose={() => setShowConfirmClose(false)}
        footer={
          <>
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.continue')} variant="secondary" onPress={() => setShowConfirmClose(false)} />
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.close')} variant="primary" onPress={confirmClose} />
          </>
        }
      >
        <Text style={styles.confirmText}>{t(currentListLanguage, 'modal.unsaved.message')}</Text>
      </AdminModal>
    </View>
  );
};

const styles = StyleSheet.create({
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.blue50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 12,
    marginBottom: 4,
  },
  infoIcon: {
    marginRight: 8,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    lineHeight: 20,
  },
  addButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    marginBottom: 4,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 4,
    gap: 12,
    flexWrap: 'wrap',
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    minWidth: 200,
    flex: 1,
    maxWidth: 300,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    outlineWidth: 0,
  } as any,
  loadingWrapper: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  globalError: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.red400,
    backgroundColor: AdminColors.red75,
    padding: 12,
    marginBottom: 8,
    borderRadius: 8,
  },
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { shadowColor: '#005EC0', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24, elevation: 2 },
    }),
    overflow: 'hidden',
    marginTop: 12,
  },
  cellText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusActive: {
    backgroundColor: AdminColors.green75,
  },
  statusInactive: {
    backgroundColor: AdminColors.gray100 ?? '#EBEBEB',
  },
  statusBadgeText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
  },
  statusActiveText: {
    color: AdminColors.green600,
  },
  statusInactiveText: {
    color: AdminColors.textMuted ?? '#737373',
  },
  modalError: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.red400,
    backgroundColor: AdminColors.red75,
    padding: 12,
    marginBottom: 12,
    borderRadius: 8,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  errorText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.red400,
    flex: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  toggleLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
    minWidth: 40,
  },
  confirmText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    marginBottom: 8,
  },
});

export default PhotoConfig;

